import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Chapters from './Chapters';
import './ChapterSection.css';
import demoImg from './assets/powerpointTemplate.jpg';
import mover from '../VideoEditor/assets/mover.svg';
import add from '../VideoEditor/assets/Add.svg'
import remove from '../VideoEditor/assets/remove.svg'
import merge from '../VideoEditor/assets/Merge.svg'
import trace from '../VideoEditor/assets/Trace.svg'
import cutChapter from '../VideoEditor/assets/cut.svg'
import pinIcon from './assets/pinIcon.svg'
import pin from './assets/pinTriangle.svg'
import { ImageActionState, PreSignedUrl, Thumbnail } from './ChapterEditorMain';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useVideoContext } from './Context/VideoContext';
import { Version } from '../types/presentation';
import { motion, useMotionValue, animate } from 'framer-motion';


interface Chapter {
    id: number;
    title: string;
    image: string;
    duration: number;
}

interface ThumbnailEndTime {
    id: number;
    endTime: number; // in milliseconds
}

interface ThumbnailDuration {
    id: number;
    duration: number; // in milliseconds
}

export type DetailsType = {
    [key: number]: {
        title: string;
        topic: string;
    };
};

interface ChapterSectionProps {
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    signedUrls: PreSignedUrl[];
    // selectedChapters: number[];
    // setSelectedChapters: React.Dispatch<React.SetStateAction<number[]>>;
    selectedChapters: ImageActionState;
    setSelectedChapters: React.Dispatch<React.SetStateAction<ImageActionState>>;
    setSignedUrls: React.Dispatch<React.SetStateAction<PreSignedUrl[]>>;
    onDataFromThird: (data: string | '') => void;
    dataFromChild: string;
    setHasModification: (value: boolean) => void;
    locale: string
    thumbnailEndTimes: ThumbnailEndTime[]
    // chapters: ChapterData[]
    // setChapters: React.Dispatch<React.SetStateAction<ChapterData[]>>
    singleVersion: Version | null;
    setDetails: React.Dispatch<React.SetStateAction<DetailsType>>
    details: DetailsType;
    removedThumbnails: string[];
    setRemovedThumbnails: React.Dispatch<React.SetStateAction<string[]>>;
    newThumbnails: NewThumbnail[];
    setNewThumbnails: React.Dispatch<React.SetStateAction<NewThumbnail[]>>;
    updateStatus: Boolean
}

export interface ChapterData {
    id: number;
    url: string;
    time: number;
    duration: number;
    width: number;
}

export interface NewThumbnail {
    id: number;
    url: string;
    time: number;
    sourceThumbId: number;
    operation: 'cut' | 'merge';
}

// Add Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        console.error('Debug Display Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

// Updated Debug Component with enhanced visuals

const ChapterSection: React.FC<ChapterSectionProps> = ({
    zoom,
    setZoom,
    signedUrls,
    selectedChapters,
    setSelectedChapters,
    setSignedUrls,
    onDataFromThird,
    dataFromChild,
    setHasModification,
    locale,
    thumbnailEndTimes,
    // chapters,
    // setChapters,
    singleVersion,
    setDetails,
    details,
    removedThumbnails,
    setRemovedThumbnails,
    newThumbnails,
    setNewThumbnails,
    updateStatus
}) => {
    const [marginTop, setMarginTop] = useState("0");
    const [scrollPosition, setScrollPosition] = useState(0);
    const [chapterId, setChapterId] = useState<number | null>(null);
    const [chapterDurations, setChapterDurations] = useState<Map<number, number>>(() =>
        new Map(signedUrls.map((_, index) => [index, 60]))
    );
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const [hoveredChapter, setHoveredChapter] = useState<number | null>(null);
    const [sliderPosition, setSliderPosition] = useState(0);
    const [position, setPosition] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [timePosition, setTimePosition] = useState(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [initialPosition, setInitialPosition] = useState<number>(0);
    const [isWindows, setIsWindows] = useState(false);
    const requestAnimationFrameIdRef = useRef<number | null>(null);
    const [activeThumbnailIndex, setActiveThumbnailIndex] = useState(0);
    const [thumbnailWidth, setThumbnailWidth] = useState<string>('15rem');
    const [initialScrollPosition, setInitialScrollPosition] = useState(0);
    const [cursorEnd, setCursorEnd] = useState(false);
    const [cursorLeft, setCursorLeft] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollSpeed, setScrollSpeed] = useState(10); 
    const tracerX = useMotionValue(25); // Initial position
    const springConfig = { damping: 50, stiffness: 500 };

    const basePixelsPerSecond = 18;
    // Scale pixels per second based on zoom level
    const pixelsPerSecond = useMemo(() => {
        return basePixelsPerSecond * (1 + (zoom / 100));
    }, [zoom]); 

    // Calculate durations based on thumbnail end times
    const calculateDurations = (endTimes: ThumbnailEndTime[]): ThumbnailDuration[] => {
        const sortedEndTimes = [...endTimes].sort((a, b) => a.id - b.id);
        const durations: ThumbnailDuration[] = [];

        for (let i = 0; i < sortedEndTimes.length; i++) {
            const duration = i === 0
                ? sortedEndTimes[i].endTime // First thumbnail's duration is its end time
                : sortedEndTimes[i].endTime - sortedEndTimes[i - 1].endTime; // Subsequent thumbnails
            durations.push({ id: sortedEndTimes[i].id, duration });
        }
        return durations;
    };

    // Get video context for controlling playback
    const { duration, isPlaying, seek } = useVideoContext();
    console.log(selectedChapters, "selectedChapters or (states)");

    useEffect(() => {
        try {
            if (selectedChapters?.selected.length >= 2) {
                // Sort selected chapters by ID to get proper range
                const sortedChapters = [...selectedChapters.selected].sort((a, b) => a - b);
                const minChapterId = sortedChapters[0];
                const maxChapterId = sortedChapters[sortedChapters.length - 1];

                // Get all chapters in the range (including ones that might not be selected)
                const chaptersInRange = selectedChapters.chapters.filter(
                    ch => ch.id >= minChapterId && ch.id <= maxChapterId
                );

                // Calculate total width of all chapters in the range
                const totalWidth = chaptersInRange.reduce((sum, chapter) => {
                    return sum + (chapter.width / 16);
                }, 0);

                setThumbnailWidth(`${totalWidth - 7}rem`);
            } else {
                setThumbnailWidth('');
            }
        } catch (error) {
            console.error(error);
        }
    }, [selectedChapters, selectedChapters.chapters]);

    useEffect(() => {
        // Detect platform
        const platform = navigator.platform.toLowerCase();
        setIsWindows(platform.includes('win'));
    }, []);

    useEffect(() => {
        const handleTimeUpdate = (event: MessageEvent) => {
            try {
                const eventData = JSON.parse(event.data);

                // Parse event data
                if (eventData.type === "progress" && eventData.data?.time !== undefined) {
                    // Use precise time value without rounding
                    setCurrentTime(eventData.data.time);
                }
            } catch (error) {
                console.error("Error parsing event data:", error);
            }
        };

        window.addEventListener('message', handleTimeUpdate);

        return () => {
            window.removeEventListener('message', handleTimeUpdate);
        };
    }, []);

    // Calculate total chapters duration
    const totalChaptersDuration = useMemo(() => {
        return Array.from(chapterDurations.values()).reduce((total, duration) => total + duration, 0);
    }, [chapterDurations]);

    // Calculate total width of all chapters - now depends on pixelsPerSecond which includes zoom
    const totalWidth = useMemo(() => {
        return totalChaptersDuration * pixelsPerSecond;
    }, [totalChaptersDuration, pixelsPerSecond]);

    const getActiveThumbnailIndex = (currentTimeMs: number): number => {
        // Sort the thumbnail end times to ensure they're in order
        // Sort chapters by their start time
        const sortedChapters = [...selectedChapters.chapters].sort((a, b) => a.time - b.time);

        // Find the chapter where currentTimeMs falls within its duration
        for (let i = 0; i < sortedChapters.length; i++) {
            const chapter = sortedChapters[i];
            const chapterEndTime = chapter.time + (chapter.duration * 1000); // Convert duration to ms

            if (currentTimeMs >= chapter.time && currentTimeMs <= chapterEndTime) {
                return chapter.id;
            }
        }

        // If beyond all chapters, return the last chapter's ID
        return sortedChapters.length > 0 ? sortedChapters[sortedChapters.length - 1].id : 0;
    };

    // Calculate cursor position based on current time
    const calculateCursorPosition = useCallback((currentTimeMs: number): number => {
        // Ensure current time doesn't exceed total duration
        const maxTimeMs = duration * 1000;
        const clampedTimeMs = Math.min(currentTimeMs, maxTimeMs);

        // Calculate position based on seconds (not milliseconds)
        const positionInSeconds = clampedTimeMs / 1000;
        return (positionInSeconds * pixelsPerSecond) + 25;
    }, [duration, pixelsPerSecond]);

    // Update cursor position based on current time
    useEffect(() => {
        if (!dragging) {
            try {
                const currentTimeMs = currentTime * 1000;
                const newPosition = calculateCursorPosition(currentTimeMs);

                // Use framer-motion's animate for smooth transitions
                if (isPlaying) {
                    // Smoother transition when playing
                    animate(tracerX, newPosition, {
                        type: "spring",
                        ...springConfig,
                        velocity: 0
                    });
                } else {
                    // Immediate transition when paused
                    tracerX.set(newPosition);
                }

                // Keep React state updated for consistency
                setPosition(newPosition);
                setTimePosition(currentTime);
                setActiveThumbnailIndex(getActiveThumbnailIndex(currentTimeMs));

            } catch (error) {
                console.error(error);
            }
        }
    }, [currentTime, dragging, duration, pixelsPerSecond, calculateCursorPosition, isPlaying]);



    // Add proper TypeScript types for debounce function
    function debounce<T extends (...args: any[]) => any>(
        func: T,
        wait: number
    ): (...args: Parameters<T>) => void {
        let timeout: NodeJS.Timeout | null = null;
        return function (this: any, ...args: Parameters<T>) {
            const context = this;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    const debouncedSeek = useCallback(
        debounce((time: number) => seek(time), 50),
        [seek]
    );

    const tracerRef = useRef<HTMLDivElement>(null);
    const positionRef = useRef(position);
    const updateScheduled = useRef(false);

    // Handle mouse move during dragging
    const handleMouseMove = (e: MouseEvent) => {
        if (!dragging) return;

        try {
            // Calculate new position
            const deltaX = e.clientX - startX;
            const newPositionAbsolute = Math.max(25, initialPosition + deltaX);

            const maxPosition = (duration * pixelsPerSecond) + 25;

            // Clamp position to prevent exceeding total duration
            const clampedPosition = Math.min(newPositionAbsolute, maxPosition);

            // Direct update the motion value for zero lag
            tracerX.set(clampedPosition);

            // Calculate time values
            const timeInSeconds = (clampedPosition - 25) / pixelsPerSecond;
            const timeInMs = timeInSeconds * 1000;
            const clampedTimeMs = Math.min(timeInMs, duration * 1000);

            // Handle scrolling
            if (scrollRef.current) {
                const container = scrollRef.current;
                const visibleStart = container.scrollLeft;
                const visibleWidth = container.clientWidth;
                const cursorDisplayPos = clampedPosition - visibleStart;

                const baseScrollSpeed = 20 * (1 + zoom / 50);
                const scrollThreshold = 150;

                if (cursorDisplayPos < scrollThreshold) {
                    const distanceFromEdge = Math.max(1, cursorDisplayPos);
                    const scrollAmount = baseScrollSpeed * (scrollThreshold / distanceFromEdge) * 0.2;
                    container.scrollLeft = Math.max(0, visibleStart - scrollAmount);
                } else if (cursorDisplayPos > visibleWidth - scrollThreshold) {
                    // Near right edge
                    const distanceFromEdge = Math.max(1, visibleWidth - cursorDisplayPos);
                    const scrollAmount = baseScrollSpeed * (scrollThreshold / distanceFromEdge) * 0.2;

                    // Calculate maximum scroll position
                    const maxScrollPosition = Math.max(0, maxPosition - visibleWidth);

                    // Ensure we don't scroll beyond the content width
                    container.scrollLeft = Math.min(maxScrollPosition, visibleStart + scrollAmount);

                    // Handle edge case - ensure we can scroll to reveal the very end
                    if (clampedPosition >= maxPosition - 50) {
                        container.scrollLeft = maxScrollPosition;
                    }
                }
            }

            // Batch state updates
            if (!updateScheduled.current) {
                updateScheduled.current = true;

                requestAnimationFrame(() => {
                    setPosition(tracerX.get());
                    setTimePosition(clampedTimeMs / 1000);
                    setActiveThumbnailIndex(getActiveThumbnailIndex(clampedTimeMs));
                    updateScheduled.current = false;
                });
            }

            // Use throttled seek for video updates
            throttledSeek(clampedTimeMs / 1000);

        } catch (error) {
            console.error(error);
        }
    };

    function throttle<T extends (...args: any[]) => any>(
        func: T,
        limit: number
    ): (...args: Parameters<T>) => void {
        let inThrottle = false;
        return function (this: any, ...args: Parameters<T>) {
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
    const throttledSeek = useCallback(
        throttle((time: number) => seek(time), 40), // 40ms throttle for 25fps updates
        [seek]
    );
    const HandleDragging = (e: React.MouseEvent) => {
        try {
            e.preventDefault();
            e.stopPropagation();

            // Update state for drag tracking
            setDragging(true);
            setStartX(e.clientX);
            setInitialPosition(tracerX.get());

            // No need for class manipulation with framer-motion
        } catch (error) {
            console.error(error);
        }
    };



    useEffect(() => {
        return () => {
            if (requestAnimationFrameIdRef.current) {
                cancelAnimationFrame(requestAnimationFrameIdRef.current);
            }
        };
    }, []);

    const handleMouseUp = () => {
        try {
            setDragging(false);

            // Final seek to ensure video position is accurate
            const timeInSeconds = (tracerX.get() - 25) / pixelsPerSecond;
            seek(timeInSeconds);
        } catch (error) {
            console.error(error);
        }
    };

    // Add event listeners for mouse move and up
    useEffect(() => {
        if (dragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, startX, initialPosition, pixelsPerSecond, totalWidth, selectedChapters.chapters]);

    // Update slider position when it changes
    useEffect(() => {
        const slider = sliderRef.current;
        if (slider) {
            slider.style.left = `${sliderPosition}px`;
        }
    }, [sliderPosition]);

    // Track scrolling of the container
    useEffect(() => {
        const handleScroll = () => {
            if (scrollRef.current) {
                setScrollPosition(scrollRef.current.scrollLeft);
            }
        };

        const scrollContainer = scrollRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    // Maintain scroll position when zoom changes
    useEffect(() => {
        if (scrollRef.current) {
            // Store the current center point of the view
            const containerWidth = scrollRef.current.clientWidth;
            const centerPoint = scrollPosition + (containerWidth / 2);
            const centerTimePosition = centerPoint / (basePixelsPerSecond * (1 + ((zoom - 1) / 100)));

            // After zoom changes, calculate the new scroll position to maintain the same center point
            const newCenterPoint = centerTimePosition * pixelsPerSecond;
            const newScrollPosition = newCenterPoint - (containerWidth / 2);

            // Apply the new scroll position
            scrollRef.current.scrollLeft = Math.max(0, newScrollPosition);
        }
    }, [zoom, pixelsPerSecond]);

    // Scroll to selected chapters
    useEffect(() => {
        if (selectedChapters?.selected.length >= 2) {
            try {

                const selectedChapterElements = selectedChapters.selected
                    .map(id => document.getElementById(`chapter-${id}`))
                    .filter(Boolean);

                if (selectedChapterElements.length) {
                    const first = selectedChapterElements[0];
                    const last = selectedChapterElements[selectedChapterElements.length - 1];
                    const container = scrollRef.current;

                    if (first && last && container) {
                        const firstRect = first.getBoundingClientRect();
                        const lastRect = last.getBoundingClientRect();
                        const containerRect = container.getBoundingClientRect();

                        const centerX = (firstRect.left + lastRect.right) / 2 - containerRect.left;
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
    }, [selectedChapters]);

    // Update chapter duration
    const updateChapterDuration = (id: number, newDuration: number) => {
        setHasModification(true);
        setChapterDurations(prev => {
            const updated = new Map(prev);
            updated.set(id, newDuration);
            return updated;
        });
    };

    // Initialize or update chapter durations when signedUrls changes
    useEffect(() => {
        setChapterDurations(prevDurations => {
            const newDurations = new Map();
            signedUrls.forEach((url, index) => {
                // Keep existing durations if available, otherwise default to 60
                newDurations.set(index, prevDurations.get(index) || 60);
            });
            return newDurations;
        });
    }, [signedUrls.length]);

    useEffect(() => {
        if (thumbnailEndTimes.length > 0) {
            const durations = calculateDurations(thumbnailEndTimes);
            const durationMap = new Map();

            durations.forEach(item => {
                durationMap.set(item.id, item.duration / 1000);
            });

            setChapterDurations(durationMap);
        }
    }, [thumbnailEndTimes]);

    // Toggle chapter selection
    const toggleChapterSelection = (id: number, type: "add" | "remove") => {
        try {

            setSelectedChapters((prevState) => {
                const prev = prevState.selected;
                let newSelection: number[];

                if (type === "add") {
                    if (prev.length === 0) {
                        newSelection = [id];
                    } else {
                        const minSelected = Math.min(...prev);
                        const maxSelected = Math.max(...prev);

                        if (id < minSelected) {
                            // Add all IDs from id to minSelected-1
                            newSelection = [
                                ...prev,
                                ...Array.from({ length: minSelected - id }, (_, i) => id + i),
                            ];
                        } else if (id > maxSelected) {
                            // Add all IDs from maxSelected+1 to id
                            newSelection = [
                                ...prev,
                                ...Array.from({ length: id - maxSelected }, (_, i) => maxSelected + i + 1),
                            ];
                        } else {
                            // If id is already in the range, keep the selection as is
                            newSelection = prev;
                        }
                    }
                } else if (type === "remove") {
                    // When removing, we need to recalculate the selection
                    // to ensure we don't have gaps in the selection
                    const remainingIds = prev.filter(chapterId => chapterId !== id);

                    if (remainingIds.length <= 1) {
                        // If only one or zero IDs remain, just use those
                        newSelection = remainingIds;
                    } else {
                        // Find the new min and max
                        const newMin = Math.min(...remainingIds);
                        const newMax = Math.max(...remainingIds);

                        // Create a continuous selection from newMin to newMax
                        newSelection = Array.from(
                            { length: newMax - newMin + 1 },
                            (_, i) => newMin + i
                        );
                    }
                } else {
                    return prevState;
                }

                return {
                    ...prevState,
                    selected: newSelection,
                };
            });
        } catch (error) {
            console.error(error);
        }
    };

    // Handle mover mouse events for resizing chapters
    const [isResizing, setIsResizing] = React.useState(false);

    // Update resize state
    const handleMoverMouseDown = (id: number, e: React.MouseEvent) => {
        try {
            e.stopPropagation();
            setIsResizing(true);
            const startX = e.clientX;
            const startWidth = selectedChapters.chapters[id].width;
            isDraggingRef.current = true;

            const hasNextThumbnail = id < selectedChapters.chapters.length - 1;
            const nextThumbnailId = hasNextThumbnail ? id + 1 : -1;
            const nextThumbnailStartWidth = hasNextThumbnail ? selectedChapters.chapters[nextThumbnailId].width : 0;

            const handleMoverMouseMove = (moveEvent: MouseEvent) => {
                if (!isDraggingRef.current) return;

                try {
                    const deltaX = moveEvent.clientX - startX;
                    let adjustedDeltaX = deltaX;

                    if (hasNextThumbnail && (nextThumbnailStartWidth - deltaX) < 50) {
                        adjustedDeltaX = nextThumbnailStartWidth - 50;
                    }

                    const newWidth = Math.max(50, startWidth + adjustedDeltaX);
                    const widthChange = newWidth - startWidth;

                    // Update next chapter width in Redux if exists
                    if (hasNextThumbnail) {
                        const nextThumbNewWidth = Math.max(50, nextThumbnailStartWidth - widthChange);
                    }

                    // Update durations
                    updateChapterDuration(id, newWidth / pixelsPerSecond);
                    if (hasNextThumbnail) {
                        updateChapterDuration(nextThumbnailId, (nextThumbnailStartWidth - widthChange) / pixelsPerSecond);
                    }

                    selectedChapters.chapters[id].width = newWidth;
                    selectedChapters.chapters[id].duration = newWidth / pixelsPerSecond;

                } catch (error) {
                    console.error('Error in handleMoverMouseMove:', error);
                }
            };

            const handleMoverMouseUp = () => {
                isDraggingRef.current = false;
                setIsResizing(false);
                document.removeEventListener('mousemove', handleMoverMouseMove);
                document.removeEventListener('mouseup', handleMoverMouseUp);
            };

            document.addEventListener('mousemove', handleMoverMouseMove);
            document.addEventListener('mouseup', handleMoverMouseUp);
        } catch (error) {
            console.error('Error in handleMoverMouseDown:', error);
        }
    };

    // console.log('hello:', chapters);
    console.log('hi selected:', selectedChapters);

    // Update margin top based on window size
    const updateMarginTop = () => {
        setMarginTop("10%"); // Use consistent margin
    };

    useEffect(() => {
        updateMarginTop(); // Set initial value
        window.addEventListener("resize", updateMarginTop); // Update on resize
        return () => window.removeEventListener("resize", updateMarginTop); // Cleanup
    }, []);

    console.log(thumbnailWidth, "thumbnails width");


    // Format seconds to MM:SS
    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Update the chapter durations when chapters change
    useEffect(() => {
        if (selectedChapters.chapters.length > 0) {
            const newDurations = new Map();
            selectedChapters.chapters.forEach(chapter => {
                newDurations.set(chapter.id, chapter.duration);
            });
            setChapterDurations(newDurations);
        }
    }, [selectedChapters.chapters]);


    // Handle merging chapters
    const handleMergeChapters = async () => {
        try {
            if (selectedChapters.selected.length < 2) {
                toast.error("Select at least two thumbnails to merge.");
                return;
            }
            const selectedChaptersData = selectedChapters.chapters.filter(chapter =>
                selectedChapters.selected.includes(chapter.id)
            );

            const firstChapter = selectedChaptersData[0];

            const mergedChapter = {
                ...firstChapter,
                duration: selectedChaptersData.reduce((total, chapter) => total + chapter.duration, 0),
                width: selectedChaptersData.reduce((total, chapter) => total + chapter.width, 0),
            };

            // Track removed thumbnails (excluding first one)
            const removedImages = selectedChaptersData.slice(1).map(chapter => ({
                index: selectedChapters.chapters.findIndex(c => c.id === chapter.id),
                image: chapter.url
            }));

            // Update chapters
            const updatedChapters = selectedChapters.chapters.filter(
                chapter => !selectedChapters.selected.includes(chapter.id) || chapter.id === firstChapter.id
            );

            const finalChapters = updatedChapters.map(chapter =>
                chapter.id === firstChapter.id ? mergedChapter : chapter
            );
            console.log(finalChapters, "final chapters");

            setSelectedChapters(prev => ({
                ...prev,
                images: finalChapters.map(ch => ch.url),
                selected: [],
                removedImages: [...prev.removedImages, ...removedImages],
                operation: 'merge',
                mergedChapter: mergedChapter,
                originalChapters: selectedChaptersData,
                chapters: finalChapters
            }));
            // setChapters(finalChapters);
            setHasModification(true);
            setSignedUrls(prevUrls =>
                prevUrls.filter((_, index) =>
                    !selectedChapters.selected.slice(1).includes(index)
                )
            );

            toast.success("Thumbnails merged successfully!");
        } catch (error) {
            console.error("Merge error:", error);
            toast.error("Error merging thumbnails.");
        }
    };

    console.log(selectedChapters, "selected chaptess");

    // Handle cutting chapter at current position
    const HandleChapterCut = (chapterId: number) => {
        try {
            // 1. Find the chapter to split
            const chapterIndex = selectedChapters.chapters.findIndex(c => c.id === chapterId);
            if (chapterIndex === -1) {
                toast.error("Chapter not found");
                return;
            }

            const originalChapter = selectedChapters.chapters[chapterIndex];
            const cutPositionSeconds = timePosition;

            // 2. Calculate absolute cut time (in milliseconds)
            const absoluteCutTimeMs = cutPositionSeconds * 1000;

            // 3. Calculate relative position within the chapter
            const relativeCutTimeMs = absoluteCutTimeMs - originalChapter.time;

            // 4. Validate the cut position
            if (relativeCutTimeMs <= 0 || relativeCutTimeMs >= (originalChapter.duration * 1000)) {
                toast.error(`Cut must be within chapter (${formatTime(0)} to ${formatTime(originalChapter.duration)})`);
                return;
            }

            // 5. Calculate durations for both parts
            const firstPartDurationSec = relativeCutTimeMs / 1000;
            const secondPartDurationSec = originalChapter.duration - firstPartDurationSec;

            // 6. Create the split chapters
            const firstPart = {
                ...originalChapter,
                duration: firstPartDurationSec,
                width: firstPartDurationSec * pixelsPerSecond
            };

            const secondPart = {
                ...originalChapter,
                id: generateNewChapterId(),
                time: absoluteCutTimeMs,
                duration: secondPartDurationSec,
                width: secondPartDurationSec * pixelsPerSecond
            };

            // 7. Update the chapters array
            const updatedChapters = [...selectedChapters.chapters];
            updatedChapters[chapterIndex] = firstPart;
            updatedChapters.splice(chapterIndex + 1, 0, secondPart);

            // 8. Reassign IDs sequentially to ensure they're in order
            const reorderedChapters = updatedChapters.map((chapter, index) => ({
                ...chapter,
                id: index
            }));

            // Store new thumbnail info
            const newCutThumbnail: NewThumbnail = {
                id: secondPart.id, // Use the ID of the second part
                url: originalChapter.url,
                time: absoluteCutTimeMs,
                sourceThumbId: originalChapter.id,
                operation: 'cut'
            };

            // Update state with undo/redo support
            setSelectedChapters(prev => ({
                ...prev,
                images: reorderedChapters.map(ch => ch.url),
                selected: [],
                removedImages: [
                    {
                        index: chapterIndex,
                        image: originalChapter.url
                    }
                ],
                operation: 'cut',
                cutPosition: absoluteCutTimeMs,
                originalChapter: originalChapter,
                splitChapters: [firstPart, secondPart],
                chapters: reorderedChapters
            }));

            // setChapters(reorderedChapters);
            setHasModification(true);
            setNewThumbnails(prev => [...prev, newCutThumbnail]);

            // Update signedUrls
            setSignedUrls(prevUrls => {
                const newUrls = [...prevUrls];
                const newUrlEntry = {
                    ...prevUrls[chapterIndex],
                    time: absoluteCutTimeMs
                };
                newUrls.splice(chapterIndex + 1, 0, newUrlEntry);
                return newUrls;
            });

            toast.success(`Split into ${formatTime(firstPartDurationSec)} + ${formatTime(secondPartDurationSec)}`);
        } catch (error) {
            console.error("Split failed:", error);
            toast.error("Failed to split chapter");
        }
    };
    const generateNewChapterId = () => Math.max(0, ...selectedChapters.chapters.map(c => c.id)) + 1;

    // Compute initial details using useMemo
    const initialDetails = useMemo(() => {
        return selectedChapters.chapters.reduce((acc, chapter, index) => {
            const thumbnail = singleVersion?.thumbnails?.[index];
            acc[chapter.id] = {
                title: thumbnail?.title || `Chapter ${chapter.id + 1}`,
                topic: thumbnail?.description || "Topic of this chapter"
            };
            return acc;
        }, {} as { [key: number]: { title: string; topic: string } });
    }, [selectedChapters.chapters, singleVersion]);

    // Initialize details state with computed initialDetails
    useEffect(() => {
        setDetails(initialDetails);
    }, [initialDetails]);

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', onWheel);
        };
    }, []);
    ///////////////////////////////////////////////////////

    return (
        <>
            {/* chapter section */}
            <div className="position-relative w-100" ref={scrollContainerRef} style={{
                padding: 0,
                margin: 0,
            }}>
                {/* Outer container for tracer and scrollable content */}
                <div className="position-relative">
                    <div ref={scrollRef} className='d-flex align-items-end'
                        style={{
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            zIndex: 0,
                            height: '15rem',
                            background: window.innerWidth <= 433 ? "#1E2024" : '',
                        }}
                    >
                        {/* Scrollable content container */}
                        {selectedChapters?.chapters.length > 0 && (
                            <div className='w-auto'>
                                <div
                                    className="text-white px-4 py-3"
                                    style={{
                                        background: '#4b4f5a',
                                        position: 'relative',
                                        width: '100vw',
                                        minWidth: 'max-content',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {/* Movable tracer - now outside the scrollable container */}
                                    {selectedChapters?.chapters.length > 0 && (
                                        <motion.div
                                            ref={tracerRef}
                                            className="chapter-cut-slider d-flex flex-column align-items-center"
                                            style={{
                                                zIndex: 10000,
                                                position: 'absolute',
                                                bottom: 0,
                                                left: tracerX,
                                                cursor: dragging ? 'grabbing' : 'grab',
                                                userSelect: 'none',
                                                touchAction: 'none',
                                                pointerEvents: 'all',
                                            }}
                                            onMouseDown={HandleDragging}
                                            // Apply high-performance optimizations
                                            layoutId="tracer"
                                            transition={{
                                                type: "spring",
                                                ...springConfig
                                            }}

                                        >
                                            {/* Top tracer icon */}
                                            <img
                                                src={trace}
                                                alt="Tracer"
                                                className="tracer-img"
                                                style={{
                                                    zIndex: 10000,
                                                    position: 'absolute',
                                                    bottom: '11rem',
                                                }}
                                            />
                                            <div className='position-relative' style={{ minHeight: '6.25rem' }}>
                                                {/* Vertical dashed line with increased height */}
                                                <div className="d-flex flex-column align-items-center position-absolute" style={{
                                                    height: '99px',
                                                    width: '2px',
                                                    pointerEvents: 'none',
                                                    zIndex: 9999,
                                                }}>
                                                    <div
                                                        className="flex-grow-1"
                                                        style={{
                                                            borderLeft: '2px dashed #24B2FF',
                                                        }}
                                                    ></div>
                                                </div>

                                                {/* Cut chapter button */}
                                                <button
                                                    className="border-1 border-white"
                                                    style={{
                                                        backgroundColor: "#32363F",
                                                        borderRadius: "5px",
                                                        height: "36px",
                                                        width: "36px",
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                                                        zIndex: 9999,
                                                        pointerEvents: 'auto'
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        HandleChapterCut(activeThumbnailIndex);
                                                    }}
                                                >
                                                    <img src={cutChapter} alt="Cut-chapter" style={{ width: "18px", height: "18px" }} />
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                    <div className="position-relative d-flex pb-4">

                                        {/* Chapter showing thumbnails showing section */}
                                        {selectedChapters?.chapters.map((chapter, index) => {

                                            return (
                                                <div className='chapter-wrapper position-relative' style={{ marginTop: '1rem' }} key={`chapter-wrapper-${chapter.id}`}>
                                                    <div
                                                        id={`chapter-${chapter.id}`}
                                                        key={chapter.id}
                                                        className={`flex-shrink-0 ${chapter.id === activeThumbnailIndex ? 'active-thumbnail' : ''}`}
                                                        style={{
                                                            width: `${chapter.width / 16}rem`,
                                                            cursor: 'pointer',
                                                            backgroundColor: selectedChapters?.selected.includes(chapter.id) ? '#000000' : '#3c3f48',
                                                            overflow: 'hidden',
                                                            transition: isResizing ? 'none' : 'width 0.3s ease'
                                                        }}
                                                        onMouseEnter={() => setHoveredChapter(chapter.id)}
                                                        onMouseLeave={() => setHoveredChapter(null)}
                                                    >
                                                        {selectedChapters?.selected.includes(chapter.id) && (
                                                            <div
                                                                className="position-absolute "
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    top: '50%',
                                                                    bottom: '43%',
                                                                    left: 0,
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                {/* Show the merge button only on the chapter with minimum ID among selected chapters */}
                                                                {selectedChapters?.selected.length >= 2 && chapter.id === Math.min(...selectedChapters?.selected) && (
                                                                    <button
                                                                        className="font-weight-bold merge-button"
                                                                        onClick={handleMergeChapters}
                                                                        style={{
                                                                            borderRadius: '5px',
                                                                            height: '40px',
                                                                            padding: '8px 16px',
                                                                            width: thumbnailWidth,
                                                                            textAlign: 'end',
                                                                            position: 'absolute',
                                                                            left: 50,
                                                                            zIndex: 35,
                                                                        }}
                                                                    >
                                                                        <img src={merge} alt="Merge" className="mr-2" />
                                                                        MERGE
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Thumbnail content */}
                                                        <div className="position-relative d-flex"
                                                            style={{
                                                                borderLeft: index === selectedChapters.chapters.length - selectedChapters.chapters.length ? 'none' : '2px solid #24B2FF',
                                                                borderRight: index === selectedChapters.chapters.length - 1 ? '2px solid #24B2FF' : 'none',
                                                            }}
                                                        >
                                                            {zoom > 0 ? (
                                                                <>
                                                                    {
                                                                        Array.from({
                                                                            length: Math.max(1, Math.ceil(chapter.width / (10 * 16) * (1 + zoom / 100)))
                                                                        }).map((_, i) => (
                                                                            <div className="position-relative" key={`thumbnail-${chapter.id}-${i}`}>
                                                                                <img
                                                                                    src={chapter?.url}
                                                                                    alt={`Chapter ${chapter.id + 1}`}
                                                                                    style={{
                                                                                        height: '6.25rem',
                                                                                        width: '10rem',
                                                                                    }}
                                                                                />
                                                                                {/* Show pin for the first thumbnail (original), pinIcon for duplicates */}
                                                                                <span
                                                                                    className="position-absolute"
                                                                                    style={{
                                                                                        top: '0',
                                                                                        right: '0',
                                                                                        zIndex: 5,
                                                                                    }}
                                                                                >
                                                                                    <img
                                                                                        src={i === 0 ? pin : pinIcon}
                                                                                        alt={i === 0 ? "Original thumbnail" : "Duplicate thumbnail"}
                                                                                    />
                                                                                </span>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </>
                                                            ) : (
                                                                <div className="position-relative" style={{ width: '100%' }}>
                                                                    <img
                                                                        src={chapter?.url}
                                                                        alt={`Chapter ${chapter.id + 1}`}
                                                                        style={{
                                                                            height: '6.25rem',
                                                                            width: '10rem',
                                                                        }}
                                                                    />
                                                                    {/* For initial single thumbnail view, always use pin */}
                                                                    <span
                                                                        className="position-absolute"
                                                                        style={{
                                                                            top: '0',
                                                                            right: '0',
                                                                            zIndex: 5,
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={pin}
                                                                            alt="Original thumbnail"
                                                                        />
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* Extra length div */}
                                                            {index % 2 === 0 && (
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        right: '-50px', // Adjust based on the extra length
                                                                        top: 0,
                                                                        width: '50px',
                                                                        height: '100%',
                                                                        backgroundColor: '#3c3f48',
                                                                    }}
                                                                ></div>
                                                            )}

                                                            {/* Selection indicator */}
                                                            {selectedChapters?.selected.includes(chapter.id) && (
                                                                <div
                                                                    className="position-absolute"
                                                                    style={{
                                                                        top: 0,
                                                                        left: 0,
                                                                        zIndex: 10, // higher than pin
                                                                        height: '100%',
                                                                        width: '100%',
                                                                        backgroundColor: '#24B2FF',
                                                                        opacity: '50%',
                                                                    }}
                                                                />
                                                            )}

                                                            {/* Chapter title on hover */}
                                                            {hoveredChapter === chapter.id && (
                                                                <div
                                                                    className="position-absolute text-center font-weight-bold"
                                                                    style={{
                                                                        top: '5px',
                                                                        width: '100%',
                                                                        zIndex: 15,
                                                                        fontSize: '12px',
                                                                        color: '#FFFFF',
                                                                    }}
                                                                >
                                                                    Chapter {index + 1}
                                                                </div>
                                                            )}

                                                            {/* Add Chapter Button */}
                                                            {!selectedChapters?.selected.includes(chapter.id) ? (
                                                                <button
                                                                    className="position-absolute d-flex align-items-center justify-content-center custom-add-button"
                                                                    style={{
                                                                        bottom: '34%',
                                                                        left: '50%',
                                                                        transform: 'translateX(-50%)',
                                                                        height: '30px',
                                                                        width: '30px',
                                                                        borderRadius: '5px',
                                                                        zIndex: 20,
                                                                        outline: 'none'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleChapterSelection(chapter.id, "add");
                                                                    }}
                                                                >
                                                                    <img src={add} alt="Add Chapter" style={{ height: '15px', width: '15px' }} />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="position-absolute d-flex align-items-center justify-content-center custom-add-button"
                                                                    style={{
                                                                        top: '40px',
                                                                        right: '20px',
                                                                        height: '30px',
                                                                        width: '30px',
                                                                        borderRadius: '5px',
                                                                        zIndex: 10,
                                                                        outline: 'none'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        toggleChapterSelection(chapter.id, "remove");
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={remove}
                                                                        alt="Remove Chapter"
                                                                        style={{
                                                                            height: '15px',
                                                                            width: '15px',
                                                                            pointerEvents: 'none'
                                                                        }}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <img
                                                        src={mover}
                                                        alt="Mover"
                                                        className="mover-icon"
                                                        onMouseDown={(e) => handleMoverMouseDown(chapter.id, e)}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '55px',
                                                            left: `${chapter.width / 16}rem`,
                                                            top: '30%',
                                                            transform: 'translateX(-50%)',
                                                            cursor: 'ew-resize',
                                                            zIndex: 10,
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Chapters showing component */}
                <div className='' style={{ background: '#4b4f5a' }}>
                    <Chapters
                        chapters={selectedChapters?.chapters.map(chapter => ({
                            id: chapter.id,
                            title: `Chapter ${chapter.id + 1}`,
                            image: demoImg,
                            duration: chapter.duration,
                            time: chapter.time
                        }))}
                        selectedChapters={selectedChapters}
                        toggleChapterSelection={toggleChapterSelection}
                        onSendData={onDataFromThird}
                        dataFromChild={dataFromChild}
                        setHasModification={setHasModification}
                        locale={locale}
                        singleVersion={singleVersion}
                        details={details}
                        setDetails={setDetails}
                        seek={seek}
                    />
                </div>
                <ToastContainer />
            </div>
        </>
    );
};

export default ChapterSection;